<!--
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="chat-panel" :class="{ 'chat-panel--minimized': minimized }">
    <!-- Header -->
    <div class="chat-panel__header">
      <div class="chat-panel__header-left">
        <PrimeButton
          v-if="showRoomList && currentRoom"
          type="button"
          icon="ri-arrow-left-line"
          :aria-label="$t('back')"
          text
          rounded
          @click="backToRoomList"
        />
        <h3 class="chat-panel__title">
          {{ headerTitle }}
        </h3>
      </div>
      <div class="chat-panel__header-actions">
        <PrimeButton
          type="button"
          :icon="minimized ? 'ri-fullscreen-line' : 'ri-subtract-line'"
          :aria-label="minimized ? $t('expand') : $t('minimize')"
          text
          rounded
          @click="toggleMinimize"
        />
        <PrimeButton
          type="button"
          icon="ri-close-line"
          :aria-label="$t('close')"
          text
          rounded
          @click="close"
        />
      </div>
    </div>

    <!-- Body -->
    <div v-show="!minimized" class="chat-panel__body">
      <!-- Connection status -->
      <div v-if="!chatStore.isReady" class="chat-panel__status">
        <!-- Connecting -->
        <div v-if="chatStore.isConnecting" class="status-connecting">
          <PrimeProgressSpinner style="width: 30px; height: 30px" />
          <span>{{ $t("chat.connecting") }}</span>
        </div>

        <!-- Reconnecting -->
        <div v-else-if="chatStore.isReconnecting" class="status-reconnecting">
          <PrimeProgressSpinner style="width: 30px; height: 30px" />
          <span>{{ $t("chat.reconnecting") }}</span>
        </div>

        <!-- Not configured -->
        <div
          v-else-if="!chatStore.hasCredentials"
          class="status-not-configured"
        >
          <i class="ri-key-line" aria-hidden="true"></i>
          <p>{{ $t("chat.notConfigured") }}</p>
          <PrimeButton
            :label="$t('chat.configureInProfile')"
            outlined
            @click="goToProfile"
          />
        </div>

        <!-- Error -->
        <div v-else-if="chatStore.lastError" class="status-error">
          <i class="ri-error-warning-line" aria-hidden="true"></i>
          <p>{{ $t("chat.connectionError") }}</p>
          <PrimeButton :label="$t('chat.retry')" outlined @click="reconnect" />
        </div>
      </div>

      <!-- Chat content -->
      <template v-else>
        <!-- Room list -->
        <ChatRoomList
          v-if="showRoomList && !currentRoom"
          :rooms="chatStore.rooms"
          :selected-room-id="chatStore.currentRoomId"
          :unread-counts="chatStore.unreadCounts"
          @select="selectRoom"
        />

        <!-- Messages view -->
        <template v-else-if="currentRoom">
          <div ref="messagesContainer" class="chat-panel__messages">
            <!-- Load more button -->
            <div v-if="canLoadMore" class="chat-panel__load-more">
              <PrimeButton
                :label="$t('chat.loadMore')"
                text
                size="small"
                :loading="loadingMore"
                @click="loadMoreMessages"
              />
            </div>

            <!-- Messages -->
            <ChatMessage
              v-for="message in chatStore.currentMessages"
              :key="message._id"
              :message="message"
              :current-user-id="currentUserId"
              @attachment-click="handleAttachmentClick"
            />

            <!-- Empty state -->
            <div
              v-if="chatStore.currentMessages.length === 0"
              class="chat-panel__empty"
            >
              <i class="ri-chat-smile-2-line" aria-hidden="true"></i>
              <p>{{ $t("chat.noMessages") }}</p>
            </div>
          </div>

          <!-- Input -->
          <ChatInput
            :disabled="!chatStore.isReady"
            :typing-users="chatStore.currentTypingUsers"
            @send="sendMessage"
            @typing="handleTyping"
            @upload="uploadFile"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import { useChatStore } from "@/stores/chat";
import ChatMessage from "@/components/chat/ChatMessage.vue";
import ChatInput from "@/components/chat/ChatInput.vue";
import ChatRoomList from "@/components/chat/ChatRoomList.vue";

export default {
  name: "ChatPanel",
  components: {
    PrimeButton: Button,
    PrimeProgressSpinner: ProgressSpinner,
    ChatMessage,
    ChatInput,
    ChatRoomList,
  },
  props: {
    showRoomList: {
      type: Boolean,
      default: true,
    },
    roomId: {
      type: String,
      default: null,
    },
  },
  emits: ["close", "minimize", "go-to-profile"],
  data() {
    return {
      minimized: false,
      loadingMore: false,
    };
  },
  computed: {
    ...mapStores(useChatStore),
    currentRoom() {
      return this.chatStore.currentRoom;
    },
    currentUserId() {
      return this.chatStore.currentUser?._id || "";
    },
    headerTitle() {
      if (this.currentRoom) {
        return this.currentRoom.fname || this.currentRoom.name;
      }
      return this.$t("chat.title");
    },
    canLoadMore() {
      return this.chatStore.currentMessages.length >= 50;
    },
  },
  watch: {
    roomId: {
      immediate: true,
      handler(newRoomId) {
        if (newRoomId) {
          this.selectRoom(newRoomId);
        }
      },
    },
    "chatStore.currentMessages": {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
    },
  },
  async mounted() {
    await this.initChat();
  },
  beforeUnmount() {
    // Don't disconnect on unmount, keep connection alive
  },
  methods: {
    async initChat() {
      if (!this.chatStore.loaded) {
        await this.chatStore.init();
      }

      if (this.chatStore.isAvailable && !this.chatStore.isReady) {
        await this.chatStore.connect();
      }
    },
    async selectRoom(roomId) {
      await this.chatStore.selectRoom(roomId);
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    backToRoomList() {
      this.chatStore.selectRoom(null);
    },
    async sendMessage(text) {
      try {
        await this.chatStore.sendMessage(text);
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("chat.sendError"),
          life: 3000,
        });
      }
    },
    handleTyping(isTyping) {
      this.chatStore.sendTypingIndicator(isTyping);
    },
    async uploadFile(file, description) {
      try {
        await this.chatStore.uploadFile(file, description);
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("chat.uploadError"),
          life: 3000,
        });
      }
    },
    async loadMoreMessages() {
      if (!this.chatStore.currentRoomId || this.loadingMore) {
        return;
      }

      this.loadingMore = true;
      const messages = this.chatStore.currentMessages;
      const oldest = messages.length > 0 ? messages[0].ts : undefined;

      try {
        await this.chatStore.loadMessages(this.chatStore.currentRoomId, {
          count: 50,
          latest: oldest,
          prepend: true,
        });
      } finally {
        this.loadingMore = false;
      }
    },
    handleAttachmentClick(attachment) {
      // Open attachment in new tab
      const url = attachment.image_url || attachment.title_link;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    toggleMinimize() {
      this.minimized = !this.minimized;
      this.$emit("minimize", this.minimized);
    },
    close() {
      this.$emit("close");
    },
    goToProfile() {
      this.$emit("go-to-profile");
    },
    async reconnect() {
      await this.chatStore.connect();
    },
  },
};
</script>

<style lang="css" scoped>
.chat-panel {
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 500px;
  background-color: var(--surface-card);
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.chat-panel--minimized {
  height: auto;
}

.chat-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: var(--primary-color);
  color: var(--primary-color-text);
}

.chat-panel__header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chat-panel__header-left :deep(.p-button) {
  color: var(--primary-color-text);
}

.chat-panel__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.chat-panel__header-actions {
  display: flex;
  gap: 0.25rem;
}

.chat-panel__header-actions :deep(.p-button) {
  color: var(--primary-color-text);
}

.chat-panel__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-panel__status {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.status-connecting,
.status-reconnecting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-color-secondary);
}

.status-not-configured,
.status-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.status-not-configured i,
.status-error i {
  font-size: 2.5rem;
  color: var(--text-color-secondary);
}

.status-error i {
  color: var(--red-500);
}

.chat-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.chat-panel__load-more {
  text-align: center;
  padding: 0.5rem;
}

.chat-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-color-secondary);
}

.chat-panel__empty i {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}
</style>
